import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { STATE_STYLE, STATE_HEX, fmtINR } from "../lib/states";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { TrendingUp, Trophy, Timer, Users2 } from "lucide-react";

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  if (!data)
    return <div className="flex-1 flex items-center justify-center text-stone-400">Loading analytics…</div>;

  const stateData = Object.entries(data.by_state).map(([name, value]) => ({ name, value }));
  const sourceData = Object.entries(data.by_source || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Analytics</h1>
          <p className="text-xs text-stone-500">Pipeline health & conversion</p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Users2} label="Total leads" value={data.total_leads} />
          <Kpi icon={Trophy} label="Won" value={data.won} />
          <Kpi icon={TrendingUp} label="Conversion rate" value={`${data.conversion_rate}%`} />
          <Kpi icon={Timer} label="Avg time to convert" value={data.avg_time_to_convert_days != null ? `${data.avg_time_to_convert_days}d` : "—"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leads by state */}
          <div className="bg-white border border-stone-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-stone-800 mb-4">Leads by stage</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stateData} margin={{ left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#78716c" }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11, fill: "#78716c" }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f5f5f4" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {stateData.map((d) => (
                    <Cell key={d.name} fill={STATE_HEX[d.name] || "#1c1917"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* By source */}
          <div className="bg-white border border-stone-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-stone-800 mb-4">Leads by source</p>
            {sourceData.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={sourceData} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#78716c" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#78716c" }} width={120} />
                  <Tooltip cursor={{ fill: "#f5f5f4" }} />
                  <Bar dataKey="value" fill="#1c1917" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-stone-400 py-10 text-center">No source data yet.</p>
            )}
          </div>
        </div>

        {/* Performance by closer */}
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <p className="text-sm font-semibold text-stone-800 mb-4">Performance by closer</p>
          {Object.keys(data.by_closer || {}).length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-stone-400 border-b border-stone-200">
                  <th className="py-2">Closer</th>
                  <th className="py-2 text-right">Deals</th>
                  <th className="py-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_closer).map(([name, v]) => (
                  <tr key={name} className="border-b border-stone-100">
                    <td className="py-2.5 text-stone-700">{name}</td>
                    <td className="py-2.5 text-right text-stone-600">{v.deals}</td>
                    <td className="py-2.5 text-right font-medium text-stone-900">{fmtINR(v.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-stone-400 py-6 text-center">
              Configure the Deal Closing CSV in Settings to see closer performance.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">{label}</p>
        <Icon className="h-4 w-4 text-stone-400" />
      </div>
      <p className="font-heading text-3xl text-stone-900 mt-2" data-testid={`kpi-${label}`}>{value}</p>
    </div>
  );
}
